
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import styles from '../core-exchange.module.scss';
export const metadata = {
  toc: true,
  title: 'Implementation checklist',
  section: 'Implementation checklist',
  layout: 'guide',
  description: 'Step-by-step checklist for implementing Plaid Core Exchange',
  parentTocLevel: 2,
  childTocLevel: 3,
  secondLevelToc: true,
  alwaysExpand: true
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <div className={styles.page}>
      <Header title='Implementation checklist' subtitle={metadata.description} mdxType="Header" />
      <h2 {...{
        "id": "scoping-and-implementation"
      }}>{`Scoping and implementation`}</h2>
      <ul>
        <li parentName="ul">
          <p parentName="li">{`Review the `}<a parentName="p" {...{
              "href": "/core-exchange/docs/reference"
            }}>{`Core Exchange API reference documentation`}</a></p>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Review the `}<a parentName="p" {...{
              "href": "/core-exchange/docs/data-mapping-guide"
            }}>{`data mapping guide`}</a>{` for FAQs and common pitfalls in data mapping`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Review the `}<a parentName="p" {...{
              "href": "#architecture-diagram"
            }}>{`architecture diagram`}</a></p>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Get access to the Plaid Data Partner Dashboard. Your Plaid contact will provide a one-time use link to either sign up for a data partner Dashboard account or create a new team if you already have Plaid Dashboard access.`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Gather business information. In the onboarding flow, Plaid verifies your organization and requires the following information, which you can collect in advance:`}</p>
          <ul parentName="li">
            <li parentName="ul">{`Name and business email address of a responsible business contact representing the data provider organization`}</li>
            <li parentName="ul">{`Company address`}</li>
            <li parentName="ul">{`Company name`}</li>
            <li parentName="ul">{`Company tax ID`}</li>
            <li parentName="ul">{`Company `}<a parentName="li" {...{
                "href": "https://www.gleif.org/en/about-lei/get-an-lei-find-lei-issuing-organizations"
              }}>{`Legal Entity Identifier`}</a></li>
            <li parentName="ul">{`Industry`}</li>
            <li parentName="ul">{`Licensing and registration (e.g., FDIC, NCUA, SIPC)`}</li>
          </ul>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Gather technical contact information. Before you go live, Plaid requires technical contact information in the event an urgent technical issue arises. For more details, see `}<a parentName="p" {...{
              "href": "/core-exchange/docs/post-launch#getting-support"
            }}>{`Post-launch operations`}</a>{`.`}</p>
          <ul parentName="li">
            <li parentName="ul">{`Shared email address`}</li>
            <li parentName="ul">{`Phone number (if available)`}</li>
            <li parentName="ul">{`Additional individual contacts`}</li>
          </ul>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Prepare one or more test accounts for each type of account you support (checking, savings, credit card, loan, 401k, etc.) Ensure that:`}</p>
          <ul parentName="li">
            <li parentName="ul">{`All accounts have balance data`}</li>
            <li parentName="ul">{`All accounts have contact data`}</li>
            <li parentName="ul">{`Depository, loan, and investment accounts have transaction data`}</li>
            <li parentName="ul">{`Depository accounts have payment networks data`}</li>
          </ul>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Allowlist the following Plaid IP addresses. `}<a parentName="p" {...{
              "href": "/core-exchange/docs/security#ip-allowlist"
            }}>{`Learn more`}</a>{` about allowlisting.`}</p>
          <ul parentName="li">
            <li parentName="ul"><inlineCode parentName="li">{`162.120.88.0/22`}</inlineCode></li>
            <li parentName="ul"><inlineCode parentName="li">{`2602:F74B::/40`}</inlineCode></li>
          </ul>
        </li>
      </ul>
      <blockquote>
        <p parentName="blockquote"><em parentName="p">{`Do not remove`}</em>{` any of the following Plaid IPs from your allowlist, if they are already present: `}<br />{` > `}<inlineCode parentName="p">{`18.214.218.91`}</inlineCode>{`, `}<inlineCode parentName="p">{`3.211.30.208`}</inlineCode>{`, `}<inlineCode parentName="p">{`3.214.25.67`}</inlineCode>{`, `}<inlineCode parentName="p">{`54.88.74.128`}</inlineCode>{`, `}<inlineCode parentName="p">{`54.208.59.10`}</inlineCode>{`,
`}<inlineCode parentName="p">{`54.88.202.28`}</inlineCode>{`, `}<inlineCode parentName="p">{`34.199.37.46`}</inlineCode>{`, `}<inlineCode parentName="p">{`35.168.137.48`}</inlineCode>{`, `}<inlineCode parentName="p">{`3.215.49.214`}</inlineCode>{`, `}<inlineCode parentName="p">{`34.202.178.138`}</inlineCode>{`,
`}<inlineCode parentName="p">{`52.0.205.192`}</inlineCode>{`, `}<inlineCode parentName="p">{`52.3.166.211`}</inlineCode>{`, `}<inlineCode parentName="p">{`35.174.147.86`}</inlineCode>{`, `}<inlineCode parentName="p">{`52.88.82.239`}</inlineCode>{`, `}<inlineCode parentName="p">{`52.41.247.19`}</inlineCode>{`,
`}<inlineCode parentName="p">{`3.233.249.56`}</inlineCode>{`, `}<inlineCode parentName="p">{`35.153.85.253`}</inlineCode>{`, `}<inlineCode parentName="p">{`3.219.116.195`}</inlineCode></p>
      </blockquote>
      <h2 {...{
        "id": "building-and-testing"
      }}>{`Building and testing`}</h2>
      <h3 {...{
        "id": "develop-and-test-authentication"
      }}>{`Develop and test authentication`}</h3>
      <ul>
        <li parentName="ul">{`Create an `}<a parentName="li" {...{
            "href": "/core-exchange/docs/authentication/oauth-server-setup"
          }}>{`OAuth server`}</a>{`, including:`}</li>
      </ul>
      <ul>
        <li parentName="ul">{`A server domain`}</li>
        <li parentName="ul">{`A `}<a parentName="li" {...{
            "href": "/core-exchange/docs/authentication/oauth-server-setup#well-known-configuration"
          }}>{`well-known configuration endpoint`}</a></li>
      </ul>
      <p>{`Note: OIDC compliance is recommended but not required. Plaid supports OAuth without OIDC. For more details, see `}<a parentName="p" {...{
          "href": "/core-exchange/docs/authentication"
        }}>{`Authentication overview`}</a>{`.`}</p>
      <ul>
        <li parentName="ul">
          <p parentName="li">{`Run through `}<a parentName="p" {...{
              "href": "/core-exchange/docs/authentication#try-the-interactive-demo"
            }}>{`FDX Explorer`}</a>{` to see a complete OAuth flow with sample requests.`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Issue Plaid a `}<a parentName="p" {...{
              "href": "/core-exchange/docs/authentication/oauth-server-setup#credentials"
            }}>{`client ID and client secret`}</a></p>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Make identity information available to Plaid (choose one):`}</p>
        </li>
      </ul>
      <ul>
        <li parentName="ul">{`OIDC compliant (recommended):`}
          <ul parentName="li">
            <li parentName="ul">
              <a href='https://openid.net/specs/openid-connect-core-1_0.html#IDToken' target='_blank'>
  ID Token + JWKS URI Route
              </a>
            </li>
            <li parentName="ul">
              <a href='https://openid.net/specs/openid-connect-core-1_0.html#UserInfo' target='_blank'>
  UserInfo Endpoint
              </a>
            </li>
          </ul>
        </li>
        <li parentName="ul">{`OAuth without OIDC:`}
          <ul parentName="li">
            <li parentName="ul"><a parentName="li" {...{
                "href": "/core-exchange/docs/reference/6.4#customerscurrent"
              }}><inlineCode parentName="a">{`/customers/current`}</inlineCode></a>{` endpoint`}</li>
          </ul>
        </li>
      </ul>
      <ul>
        <li parentName="ul">{`Create a `}<a parentName="li" {...{
            "href": "/core-exchange/docs/authentication/oauth-flow/#step-4---plaid-requests-an-access-token"
          }}>{`token exchange`}</a>{` endpoint`}</li>
      </ul>
      <h3 {...{
        "id": "develop-and-test-fdx-data-subsets"
      }}>{`Develop and test FDX data subsets`}</h3>
      <p>{`Use the `}<a parentName="p" {...{
          "href": "/core-exchange/docs/testing"
        }}>{`Validator`}</a>{` in the Dashboard to test each endpoint as you build.`}</p>
      <ul>
        <li parentName="ul">
          <p parentName="li"><a parentName="p" {...{
              "href": "/core-exchange/docs/reference/6.4#accounts"
            }}><inlineCode parentName="a">{`/accounts`}</inlineCode></a>{`: Search and view customer accounts`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li"><a parentName="p" {...{
              "href": "/core-exchange/docs/reference/6.4#accountsaccountid"
            }}><inlineCode parentName="a">{`/accounts/{accountId}`}</inlineCode></a>{`: Get account balances, liabilities, and other information`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li"><a parentName="p" {...{
              "href": "/core-exchange/docs/reference/6.4#accountsaccountidpayment-networks"
            }}><inlineCode parentName="a">{`/accounts/{accountId}/payment-networks`}</inlineCode></a>{`: Get payment networks supported by an account`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li"><a parentName="p" {...{
              "href": "/core-exchange/docs/reference/6.4#accountsaccountidcontact"
            }}><inlineCode parentName="a">{`/accounts/{accountId}/contact`}</inlineCode></a>{`: Get account contact information`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li"><a parentName="p" {...{
              "href": "/core-exchange/docs/reference/6.4#accountsaccountidtransactions"
            }}><inlineCode parentName="a">{`/accounts/{accountId}/transactions`}</inlineCode></a>{`: List all account transactions`}</p>
          <p parentName="li">{`Send both pending and posted transactions. Set `}<inlineCode parentName="p">{`status`}</inlineCode>{` to `}<inlineCode parentName="p">{`PENDING`}</inlineCode>{` while a transaction is still processing (Plaid treats `}<inlineCode parentName="p">{`AUTHORIZATION`}</inlineCode>{` and `}<inlineCode parentName="p">{`MEMO`}</inlineCode>{` the same as `}<inlineCode parentName="p">{`PENDING`}</inlineCode>{`), and to `}<inlineCode parentName="p">{`POSTED`}</inlineCode>{` once it clears. Use a different `}<inlineCode parentName="p">{`transactionId`}</inlineCode>{` for the pending and posted versions of the same transaction.`}</p>
          <p parentName="li">{`When a pending transaction settles, set `}<inlineCode parentName="p">{`referenceTransactionId`}</inlineCode>{` on the posted transaction to the `}<inlineCode parentName="p">{`transactionId`}</inlineCode>{` of the pending transaction it replaces. That link is how Plaid recognizes the two records as the same transaction. Once you have sent the posted version, you may omit the pending one from subsequent responses.`}</p>
          <p parentName="li">{`Use `}<inlineCode parentName="p">{`referenceTransactionId`}</inlineCode>{` only to link pending and posted transactions. Reverse postings and correction transactions are not linked this way — send each as an independent credit transaction.`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li"><a parentName="p" {...{
              "href": "/core-exchange/docs/reference/6.4#accountsaccountidstatements"
            }}><inlineCode parentName="a">{`/accounts/{accountId}/statements`}</inlineCode></a>{`: Get account statements`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li"><a parentName="p" {...{
              "href": "/core-exchange/docs/reference/6.4#accountsaccountidasset-transfer-networks"
            }}><inlineCode parentName="a">{`/accounts/{accountId}/asset-transfer-networks`}</inlineCode></a>{`: Get asset transfer networks for an account`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li"><a parentName="p" {...{
              "href": "/core-exchange/docs/reference/6.4#customerscurrent"
            }}><inlineCode parentName="a">{`/customers/current`}</inlineCode></a>{` (only if OIDC is not used): Get the ID of the customer within the authorization scope`}</p>
        </li>
      </ul>
      <h2 {...{
        "id": "go-live"
      }}>{`Go-live`}</h2>
      <h3 {...{
        "id": "get-ready-for-production"
      }}>{`Get ready for production`}</h3>
      <ul>
        <li parentName="ul">
          <p parentName="li">{`Confirm you have implemented strong authentication in your OAuth flow (i.e., MFA)`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Confirm your organization's name, logo, and URL. Plaid uses this information to populate your organization's details in Plaid Link. Your logo file must be a 96x96 or 152x152 circular PNG and under 2MB.`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Once you have validated your integration, request production access in the `}<a parentName="p" {...{
              "href": "https://dashboard.plaid.com/"
            }}>{`Plaid Dashboard`}</a>{`. For more details, see `}<a parentName="p" {...{
              "href": "/core-exchange/docs/production-access"
            }}>{`Production access`}</a>{`.`}</p>
        </li>
      </ul>
      <h3 {...{
        "id": "pilot-and-migrate"
      }}>{`Pilot and migrate`}</h3>
      <p><em parentName="p">{`(This section only applies to existing Plaid partners)`}</em></p>
      <p>{`If your institution is already supported in `}<a parentName="p" {...{
          "href": "/core-exchange/docs/user-experience/"
        }}>{`Plaid Link`}</a>{` or you are a `}<a parentName="p" {...{
          "href": "/core-exchange/docs/onboarding-guide#integration-models-single-institution-vs-platform"
        }}>{`platform provider`}</a>{` with numerous institutions, Plaid will partner with you to develop a pilot and migration plan.`}</p>
      <p>{`A typical migration involves:`}</p>
      <ol>
        <li parentName="ol"><strong parentName="li">{`Pilot cohort:`}</strong>{` Enable new connections in production for a small cohort, monitor health, and resolve issues.`}</li>
        <li parentName="ol"><strong parentName="li">{`Remaining cohorts:`}</strong>{` Expand to remaining cohorts in production, with continued monitoring. The number of cohorts is partner-dependent.`}</li>
        <li parentName="ol"><strong parentName="li">{`Existing user migration:`}</strong>{` After integration health is validated, Plaid begins a process to migrate all existing Items over to the OAuth + API connection. This phase typically runs over approximately 90 days.`}</li>
      </ol>
      <p><em parentName="p">{`Timelines vary based on ability to validate institutional health and address open issues.`}</em></p>
      <h2 {...{
        "id": "ongoing-management"
      }}>{`Ongoing management`}</h2>
      <p>{`After going live, Plaid continues to partner with you to ensure integration quality. Key integration health metrics include: conversion, data access success rate, data availability, and accuracy.`}</p>
      <ul>
        <li parentName="ul">
          <p parentName="li">{`Update the Plaid team with the correct point of contact to support ongoing integration health`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Log key identifiers to aid in troubleshooting. For a full list, see `}<a parentName="p" {...{
              "href": "/core-exchange/docs/post-launch#logging-identifiers-for-troubleshooting"
            }}>{`Post-launch operations`}</a>{`.`}</p>
        </li>
        <li parentName="ul">
          <p parentName="li">{`Consider ways to take your integration to the next level:`}</p>
          <ul parentName="li">
            <li parentName="ul">{`Increase user throughput with `}<a parentName="li" {...{
                "href": "/core-exchange/docs/authentication/app2app"
              }}>{`App2App functionality`}</a></li>
            <li parentName="ul">{`Get visibility into connected applications with the `}<a parentName="li" {...{
                "href": "/core-exchange/docs/permissions-manager"
              }}>{`App Directory`}</a>{`, and into user consent with `}<a parentName="li" {...{
                "href": "/core-exchange/docs/consent-management"
              }}>{`consent management`}</a></li>
          </ul>
        </li>
      </ul>
      <h2 {...{
        "id": "key-considerations-for-digital-banking-platforms"
      }}>{`Key considerations for digital banking platforms`}</h2>
      <p>{`Digital banking platforms can onboard multiple financial institutions under a single integration.`}</p>
      <h3 {...{
        "id": "opt-out-migration"
      }}>{`Opt-out migration`}</h3>
      <p>{`An "opt-out" migration is a strategy where API access is rolled out to all financial institutions on the platform by default, rather than requiring each institution to individually opt in. This approach accelerates the timeline and delivers reliable, secure data access more quickly.`}</p>
      <h4 {...{
        "id": "how-to-execute"
      }}>{`How to execute`}</h4>
      <ul>
        <li parentName="ul">{`Communicate with your financial institutions that Plaid API access is rolling out for all institutions`}</li>
        <li parentName="ul">{`Share with Plaid the number of institutions you support`}</li>
        <li parentName="ul">{`Determine if cohort-based ramping of institutions is needed (details to consider in `}<a parentName="li" {...{
            "href": "#consider-backend-data-complexities-upfront"
          }}>{`step 2`}</a>{`)`}</li>
        <li parentName="ul">{`Dependent on the scale of your existing Plaid volume, Plaid may recommend either:`}
          <ul parentName="li">
            <li parentName="ul"><strong parentName="li">{`Production pilot`}</strong>{`: Bring 1-5 financial institutions live in production to validate integration before bringing the rest live.`}</li>
            <li parentName="ul"><strong parentName="li">{`Broader rollout`}</strong>{`: Upload financial institutions into the `}<a parentName="li" {...{
                "href": "https://dashboard.plaid.com/"
              }}>{`Data Partner Dashboard`}</a>{` and launch on API.`}</li>
          </ul>
        </li>
      </ul>
      <h3 {...{
        "id": "consider-backend-data-complexities-upfront"
      }}>{`Consider backend data complexities upfront`}</h3>
      <ul>
        <li parentName="ul"><strong parentName="li">{`Pilot/testing plan:`}</strong>{` Do you have different backend pods, product lines, or backend core groupings that might align to different backend data models? If so, representing each subgroup in a pilot cohort helps uncover variability or errors early.`}</li>
        <li parentName="ul"><strong parentName="li">{`Account and routing numbers:`}</strong>{` Accuracy of account and routing numbers is critical, particularly for platforms representing different backend cores where edge-case discrepancies may exist. Validate that `}<inlineCode parentName="li">{`/accounts/{accountId}/payment-networks`}</inlineCode>{` data is accurate for all institutions before going live.`}</li>
      </ul>
      <h2 {...{
        "id": "architecture-diagram"
      }}>{`Architecture diagram`}</h2>
      <p>{`This sequence diagram depicts the standard flow of an end-user selecting an institution in Plaid Link and the resulting interaction flow between Plaid and a data partner's API.
Core Exchange offers flexibility that meets your needs, so exact flows can vary depending on your OAuth 2.0 and FDX implementation.`}</p>
      <Image src='/assets/img/core-exchange/sequence-diagram.png' alt='Sequence diagram showing the interaction flow between a user, Plaid Link, Plaid servers, and the data partner API during OAuth authentication and data retrieval' mdxType="Image" />
    </div>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "scoping-and-implementation",
        level: 2,
        title: "Scoping and implementation",
      },
{
        id: "building-and-testing",
        level: 2,
        title: "Building and testing",
      },
{
        id: "develop-and-test-authentication",
        level: 3,
        title: "Develop and test authentication",
      },
{
        id: "develop-and-test-fdx-data-subsets",
        level: 3,
        title: "Develop and test FDX data subsets",
      },
{
        id: "go-live",
        level: 2,
        title: "Go-live",
      },
{
        id: "get-ready-for-production",
        level: 3,
        title: "Get ready for production",
      },
{
        id: "pilot-and-migrate",
        level: 3,
        title: "Pilot and migrate",
      },
{
        id: "ongoing-management",
        level: 2,
        title: "Ongoing management",
      },
{
        id: "key-considerations-for-digital-banking-platforms",
        level: 2,
        title: "Key considerations for digital banking platforms",
      },
{
        id: "opt-out-migration",
        level: 3,
        title: "Opt-out migration",
      },
{
        id: "how-to-execute",
        level: 4,
        title: "How to execute",
      },
{
        id: "consider-backend-data-complexities-upfront",
        level: 3,
        title: "Consider backend data complexities upfront",
      },
{
        id: "architecture-diagram",
        level: 2,
        title: "Architecture diagram",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
